import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  AutoPricingInputs,
  Event,
  Feature,
  PricingClient,
} from 'src/WebApiController';

import { useUserHasFeature } from '../useUserHasFeature';

export type ListingGroupPricingSettingsWithListingGroupId =
  AutoPricingInputs & {
    listingGroupId: string;
  };

export const useListingGroupPricingSettings = (
  listingGroupId: string | null | undefined,
  event: Event | null | undefined
) => {
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);

  const { activeAccountWebClientConfig } = useAppContext();

  const { showErrorDialog } = useClientLogContext();

  const queryClient = useQueryClient();
  const queryKey = [
    'PricingClient.getListingGroupAutoPricingSettings',
    listingGroupId,
    event?.viagId,
  ];

  const shouldQuery = Boolean(
    activeAccountWebClientConfig.activeAccountId &&
    listingGroupId &&
    event?.viagId &&
    hasAutoPricingFeature
  );
  const listingGroupPricingSettings = useQuery({
    queryKey,
    queryFn:
      async (): Promise<ListingGroupPricingSettingsWithListingGroupId | null> => {
        if (!shouldQuery) {
          return null;
        }

        const listingGroupPricingSettings = await new PricingClient(
          activeAccountWebClientConfig
        ).getListingGroupAutoPricingSettings(listingGroupId!, event!.viagId!);

        return {
          ...listingGroupPricingSettings,
          listingGroupId: listingGroupId!,
        };
      },

    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(500 * attemptIndex, 3000),
    meta: {
      // TODO: temporally hide the error for now, enable this after https://stubhub.enterprise.slack.com/archives/C08D76X6M0S is fixed.
      // onError: (error: ErrorTypes) => {
      //   trackError('PricingClient.getListingGroupAutoPricingSettings', error, {
      //     listingGroupId,
      //   });
      // },
    },
  });

  const resetListingPricingSettingsMutation = useMutation({
    mutationFn: async () => {
      return await new PricingClient(
        activeAccountWebClientConfig
      ).resetListingAutoPricingSettingsInGroup(listingGroupId!);
    },
    onError: (err: ErrorTypes) => {
      showErrorDialog(
        'PricingClient.resetListingAutoPricingSettingsInGroup',
        err,
        {
          trackErrorData: { listingGroupId },
        }
      );
    },
  });

  const mergeListingGroupPricingSettingsMutation = useMutation({
    mutationFn: async ({
      inputs,
      listingGroupId,
      viagogoEventId,
    }: {
      inputs: AutoPricingInputs;
      listingGroupId: string | null;
      viagogoEventId: number | null;
    }) => {
      return await new PricingClient(
        activeAccountWebClientConfig
      ).mergeListingGroupPricingSettings(
        listingGroupId ?? undefined,
        viagogoEventId ?? undefined,
        inputs
      );
    },
    onMutate: ({ inputs }) => {
      queryClient.cancelQueries({ queryKey });
      const prevValue = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, inputs);
      return { prevValue };
    },
    onError: (err: ErrorTypes, { inputs }, context) => {
      queryClient.setQueryData(queryKey, context?.prevValue);
      showErrorDialog('PricingClient.mergeListingGroupPricingSettings', err, {
        trackErrorData: { inputs },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const onMergeListingGroupPricingSettings = useCallback(
    async (inputs?: AutoPricingInputs) => {
      if (!listingGroupId || !inputs) {
        return;
      }
      await mergeListingGroupPricingSettingsMutation.mutateAsync({
        listingGroupId,
        viagogoEventId: event?.viagId ?? null,
        inputs,
      });
    },
    [event?.viagId, listingGroupId, mergeListingGroupPricingSettingsMutation]
  );

  const onResetListingPricingSettings = useCallback(async () => {
    if (!listingGroupId) {
      return;
    }
    return await resetListingPricingSettingsMutation.mutateAsync();
  }, [listingGroupId, resetListingPricingSettingsMutation]);

  return {
    pricingSettings: listingGroupPricingSettings.data,
    onMergeListingGroupPricingSettings,
    onResetListingPricingSettings,
    isLoading:
      listingGroupPricingSettings.isPending ||
      resetListingPricingSettingsMutation.isPending ||
      mergeListingGroupPricingSettingsMutation.isPending,
    isPricingSettingsLoading: listingGroupPricingSettings.isLoading,
  };
};
